import dayjs from '~/timeInit';

import { calcMargin, netToAud } from '../packageOptions/marginRules';
import { BASIC_CATEGORY_INDEX } from '../packageOptions/packageCategories';
import type { OptionState } from '../packageOptions/types';

import type { OtaSummaryMonth, OtaSummaryOption } from './types';

// Room before stay: taking the stay first lets a pricier room that alone sells a shorter stay lead.
export function leadNights(option: OptionState | undefined): number {
  for (const room of option?.roomsByCategory[BASIC_CATEGORY_INDEX] ?? []) {
    const priced = option.lengthsOfStay.filter(
      (nights) => Number(option.pricingByRoomId[room.id]?.sellByLos[nights]) > 0,
    );
    if (priced.length) {
      return Math.min(...priced);
    }
  }
  return 0;
}

export type Lead = { nights: number; sell: number; net: number | undefined; roomAssetId: string };

export function leadOf(option: OptionState | undefined): Lead | null {
  const nights = leadNights(option);
  const lead = leadSellAndNet(option, nights);
  return lead ? { nights, ...lead } : null;
}

export function leadSellAndNet(
  option: OptionState | undefined,
  nights: number,
): { sell: number; net: number | undefined; roomAssetId: string } | null {
  if (!option || nights <= 0) {
    return null;
  }

  const rooms = option.roomsByCategory[BASIC_CATEGORY_INDEX] ?? [];
  for (const room of rooms) {
    const pricing = option.pricingByRoomId[room.id];
    const sell = Number(pricing?.sellByLos[nights]);
    if (sell > 0) {
      const rawNet = pricing?.netByLos[nights];
      const parsedNet = rawNet === undefined || rawNet === '' ? Number.NaN : Number(rawNet);
      return { sell, net: Number.isFinite(parsedNet) ? parsedNet : undefined, roomAssetId: room.assetId };
    }
  }

  return null;
}

export const NO_PRV_NOTE = "No PRV data for this option. Upload the workbook to fill Option 1's.";

export type PrvMonthRow = Omit<OtaSummaryMonth, 'month'> & { label: string };

export function monthLabelOf(month: string): string {
  return dayjs.utc(month, 'YYYY-MM').format('MMM-YY');
}

// The workbook's PRV is for its own lead stay, so it only applies to a lead of that stay.
export function workbookSummaryFor(
  lead: Lead | null,
  summary: OtaSummaryOption | undefined,
): OtaSummaryOption | undefined {
  return lead && summary?.nights === lead.nights ? summary : undefined;
}

export interface OptionPricingSummary {
  optionIndex: number;
  sell: number | null;
  net: number | null;
  marginPct: number | null;
  prvPct: number | null;
  monthRows: Array<PrvMonthRow>;
  stayMismatch: { workbookNights: number | null; leadNights: number } | null;
}

export function computeOptionPricingSummary({
  optionIndex,
  option,
  workbookSummary,
  netCurrency = 'AUD',
  audToSourceCurrencyRate = { AUD: 1 },
}: {
  optionIndex: number;
  option?: OptionState;
  workbookSummary?: OtaSummaryOption;
  netCurrency?: string;
  audToSourceCurrencyRate?: Record<string, number | undefined>;
}): OptionPricingSummary {
  const lead = leadOf(option);
  const netAud = lead?.net != null ? netToAud(lead.net, netCurrency, audToSourceCurrencyRate) : undefined;
  const margin = lead && netAud != null ? calcMargin(lead.sell, netAud) : undefined;
  const figures = workbookSummaryFor(lead, workbookSummary);

  return {
    optionIndex,
    sell: lead?.sell ?? null,
    net: netAud ?? null,
    marginPct: margin?.pct ?? null,
    prvPct: figures?.genuinePrv.avgPct ?? null,
    monthRows: (figures?.months ?? []).map(({ month, ...monthFigures }) => ({
      label: monthLabelOf(month),
      ...monthFigures,
    })),
    stayMismatch:
      lead && workbookSummary && !figures ? { workbookNights: workbookSummary.nights, leadNights: lead.nights } : null,
  };
}

// With no daily values the counts read as blank, not 0 of 0.
export function workbookLeadSellPrv(summary: OtaSummaryOption | undefined) {
  const total = summary?.dailyPrv.total || null;
  return {
    avgPrv: summary?.genuinePrv.avgPct ?? null,
    minPrv: summary?.genuinePrv.q1Pct ?? null,
    maxPrv: summary?.genuinePrv.q3Pct ?? null,
    avgSellVsMinOtaPct: summary?.sellVsMinOta.avgPct ?? null,
    below30PrvCount: total && summary.dailyPrv.below30,
    below30PrvTotal: total,
    negativePrvCount: total && summary.dailyPrv.negative,
    negativePrvTotal: total,
  };
}
